import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { DataDogService } from '@app/services/data-dog.service';
import * as DriverActions from '@app/store/driver/actions/driver.actions';
import { DriverApiService } from '@app/modules/driver/services/driver-api.service';
import { switchMap, map, catchError, filter, withLatestFrom } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { environment as env } from '@environments/environment';
import { DriverAssetService } from '@app/modules/driver/services/driver-asset.service';
import { Store } from '@ngrx/store';
import { AppState } from '@app/store';
import { convertToDriverAssets } from '@app/modules/shared/utilities/utilities';
import * as moment from 'moment';
import { selectDrivers } from '../selectors/driver.selector';
import { ApiDriverAsset, ApiDriverAssetResponse } from '@app/modules/driver/models/driver.models';

@Injectable()
export class DriverEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private datadog: DataDogService,
    private driverApiService: DriverApiService,
    private driverAssetService: DriverAssetService
  ) {}

  isTestingDriverProfileError = false;

  loadDrivers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.loadDrivers),
      filter(({ accountId }) => Boolean(accountId)),
      switchMap(({ accountId }) => {
        return this.driverApiService.getDriverProfilesByAccount(accountId);
      }),
      map(drivers => DriverActions.loadDriversSuccess({ drivers })),
      catchError(() => {
        return of(DriverActions.loadDriversFailure());
      })
    );
  });

  loadDriverProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.loadDriverProfile),
      filter(({ driverProfileId }) => Boolean(driverProfileId)),
      switchMap(({ driverProfileId }) => {
        // return this.driverApiService.getDriverProfile(driverProfileId).pipe(
        return this.driverApiService.getDriverProfileByCardNumber(driverProfileId).pipe(
          switchMap(driverProfile => {
            // for testing purposes only
            if (!env.production) {
              if (this.isTestingDriverProfileError) {
                return throwError('test error');
              }
            }
            return of(driverProfile);
          }),
          map(driverProfile => {
            return DriverActions.loadDriverProfileSuccess({ driverProfile });
          }),
          catchError(driverProfileFailure => {
            this.datadog.addRumError(driverProfileFailure);
            return of(DriverActions.loadDriverProfileFailure({ driverProfileFailure }));
          })
        );
      })
    );
  });

  loadDriverAssetProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DriverActions.loadDriverAssetProfile),
      withLatestFrom(this.store.select(selectDrivers)),
      switchMap(([action, driversMap]) => {
        const startTimeBacktoZero = moment(action.asset.startTime).startOf('day').toDate();
        const endTime = moment(startTimeBacktoZero).endOf('day').toDate();
        return this.driverAssetService
          .getDriverDetails(
            action.asset.companyId,
            action.asset.assetId,
            startTimeBacktoZero.toISOString(),
            endTime.toISOString()
          )
          .pipe(
            map((driverAssetProfiles: ApiDriverAssetResponse) => {
              return DriverActions.loadDriverAssetProfileSuccess({
                driverAssetProfile: convertToDriverAssets(driverAssetProfiles, driversMap)
              });
            }),
            catchError(driverAssetProfileFailure => {
              this.datadog.addRumError(driverAssetProfileFailure);
              return of(DriverActions.loadDriverAssetProfileFailure({ driverAssetProfileFailure }));
            })
          );
      })
    );
  });
}
